<template>
    <!-- Email input field -->
    <div :class="emailInputClass">
        <label class="input__label" for="email-address">
            {{ selectedLabel }}
        </label>
        <input
            id="email-address"
            name="email"
            autocomplete="email"
            autocapitalize="off"
            type="text"
            class="input__field"
            v-model.trim.lazy="email">

        <!-- Validation -->
        <p class="input__message" v-if="showRequiredValidationMessage"
           v-text="$t('EMAIL_ADDRESS_FIELD_PLEASE_PUT')"></p>
        <p class="input__message" v-if="showIsEmailUniqueValidationMessage">
            <span class="block" v-text="$t('EMAIL_ADDRESS_FIELD_LOOKS_LIKE')"></span>
            <span class="mt-3 block">
                {{ $t('EMAIL_ADDRESS_FIELD_PLEASE') }}
                <router-link :to="{ name: 'login' }" class="link text-xs">
                    {{ $t('EMAIL_ADDRESS_FIELD_LOG_IN') }}
                </router-link>.
            </span>
        </p>
        <p class="input__message" v-if="showInvalidEmailValidationMessage"
           v-text="$t('EMAIL_ADDRESS_FIELD_HMM')"></p>
    </div>
</template>
<script>
import debounce from 'lodash/debounce';
import required from 'vuelidate/src/validators/required';
import email from 'vuelidate/src/validators/email';
import { isEmailUnique } from '@/validators/EmailValidator';

export default {
    name: 'LegacyEmailAddressField',
    props: {
        requireUnique: {
            default: false,
            type: Boolean,
        },
        value: {
            default: null,
            type: String,
        },
        label: {
            default: null,
            type: String,
        },
    },
    data() {
        return {
            apiValidation: {
                email: {
                    isEmailUnique: true,
                },
            },
            email: null,
            selectedLabel: null,
        };
    },
    computed: {
        canValidateUniqueEmail() {
            return this.requireUnique && this.$v.email.email && this.$v.email.required;
        },

        emailInputClass() {
            if (this.email === null) return 'input';

            return this.isEmailValid ? 'input input--success' : 'input input--invalid';
        },

        isEmailValid() {
            return !this.$v.$invalid;
        },

        showRequiredValidationMessage() {
            return this.email !== null && !this.$v.email.required;
        },

        showInvalidEmailValidationMessage() {
            return !this.$v.email.email;
        },

        showIsEmailUniqueValidationMessage() {
            if (this.showInvalidEmailValidationMessage) return false;

            return !this.$v.email.isEmailUnique;
        },
    },
    mounted() {
        this.email = this.value;
        this.selectedLabel = this.label || this.$t('ACCOUNT_FORM_EMAIL');
    },
    methods: {
        /**
         * Check if another user hasn't registered with the requested email address.
         *
         * @param {String} email
         *
         * @returns {void}
         */
        async isEmailAvailable(email) {
            let status = await isEmailUnique(email);

            this.updateApiValidationStatus(status);
            this.updateInput();
        },

        /**
         * Update email api validation status.
         *
         * @param {Boolean} status
         *
         * @returns {void}
         */
        updateApiValidationStatus(status) {
            this.apiValidation.email.isEmailUnique = status;
        },

        /**
         * Sync with parent component.
         *
         * @returns {void}
         */
        updateInput() {
            this.$v.$touch();

            this.$emit('input', this.email);
            this.$emit('update:validation', this.isEmailValid);
        },
    },
    validations: {
        email: {
            required,
            email,
            isEmailUnique() {
                return this.apiValidation.email.isEmailUnique;
            },
        },
    },
    watch: {
        email: debounce(function(value) {
            this.canValidateUniqueEmail ? this.isEmailAvailable(value) : this.updateInput();
        }, 500),
        value(newValue) {
            if (newValue !== this.email) {
                this.email = newValue;
            }
        },
    },
};
</script>
